import React, { useMemo, useState, useEffect, useRef } from "react";
import { useDebounce } from "../../../hooks/useDebounce";


// Base table component
import { DataTable, TableColumnType, TableSecondaryAction } from "components/organisms/DataTable";
import MDBox from "components/atoms/MDBox/MDBox";
import MDTypography from "components/atoms/MDTypography/MDTypography";
import PaginationControls from "components/molecules/PaginationControls/PaginationControls";
import MDInput from "components/atoms/MDInput/MDInput";
import MDButton from "components/atoms/MDButton/MDButton";
import InputAdornment from '@mui/material/InputAdornment';
import SearchIcon from '@mui/icons-material/Search';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import ActionSlugs from 'services/PermissionsService';
import DateRangePicker, { DateRange } from "components/molecules/DateRangePicker";
import { MUIColors } from 'models/StyleModels';
import colors from "assets/theme/base/colors";
import { ClearFiltersButton } from "./TriageDataTable.styles";


interface TriageDataTableProps {
  entriesPerPage?: {
    defaultValue: number;
    entries: number[];
  };
  canSearch?: boolean;
  showTotalEntries?: boolean;
  table: {
    columns: TableColumnType[];
    rows: { [key: string]: React.ReactNode }[];
  };
  pagination?: {
    variant: 'contained' | 'gradient';
    color: string;
  };
  isSorted?: boolean[];
  noEndBorder?: boolean;
  dataLoading?: boolean;
  dataError?: boolean;
  handleRetry?(): void;
  sx?: { [key: string]: any };
  secondaryAction?: TableSecondaryAction | null;
  handleRowClick?(args: any): any;
  emptyMessage?: string;

  // Additional props for triage functionality
  org?: { use_pros?: boolean };
  user?: any;
  verticalAlign?: 'top' | 'middle';
  initialSort?: {
    key: string;
    direction: 'asc' | 'desc';
  };

  // Optional pagination props
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  totalCount?: number | null;
  totalReturned?: number;
  pageSize?: number;
  onSearchChange?: (query: string) => void;
  searchValue?: string;
  onAddExam?: () => void;
  useServerSideSearch?: boolean;
  onDateRangeChange?: (dateRange: DateRange) => void;
  dateRange?: DateRange;
}

const TriageDataTable: React.FC<TriageDataTableProps> = ({
  entriesPerPage,
  canSearch,
  showTotalEntries,
  table,
  isSorted,
  noEndBorder,
  dataLoading,
  dataError,
  handleRetry,
  secondaryAction,
  handleRowClick,
  emptyMessage,

  org,
  user,
  verticalAlign,
  initialSort,
  isFetchingNextPage = false,
  hasNextPage = false,
  onLoadMore,
  totalCount,
  totalReturned,
  pageSize = 15,
  onSearchChange,
  searchValue,
  onAddExam,
  useServerSideSearch = false,
  onDateRangeChange,
  dateRange,
}) => {
  // Pagination state (only used when onLoadMore is provided)
  const [currentDisplayPage, setCurrentDisplayPage] = useState(0);
  const [pendingPage, setPendingPage] = useState<number | null>(null);
  const [pendingPageRowCount, setPendingPageRowCount] = useState<number | null>(null);
  const displayPageSize = 15;
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const inputRef = useRef<HTMLInputElement | null>(null);


  // Ensure input remains focused after parent-controlled search value updates
  useEffect(() => {
    if (typeof searchValue === 'string') {
      inputRef.current?.focus();
    }
  }, [searchValue]);

  // Reset page and pending state when search or date range changes
  useEffect(() => {
    setCurrentDisplayPage(0);
    setPendingPage(null);
    setPendingPageRowCount(null);
  }, [searchQuery, dateRange]);

  // This manages state so that the pagination is subtle and smooth.
  useEffect(() => {
    if (onLoadMore && pendingPage !== null && !isFetchingNextPage && !searchQuery) {
      const hasNewData = pendingPageRowCount !== null && table.rows.length > pendingPageRowCount;

      if (hasNewData) {
        setCurrentDisplayPage(pendingPage);
        setPendingPage(null);
        setPendingPageRowCount(null);
      } else {
        // Waiting for new data to arrive
      }
    }
  }, [onLoadMore, pendingPage, isFetchingNextPage, currentDisplayPage, table.rows.length, pendingPageRowCount, searchQuery]);

  // Keeps users from getting stuck on a page that doesn't exist.
  useEffect(() => {
    if (onLoadMore && !searchQuery && totalCount && currentDisplayPage > 0) {
      const totalPages = Math.ceil(totalCount / displayPageSize);
      if (currentDisplayPage >= totalPages) {
        setCurrentDisplayPage(Math.max(0, totalPages - 1));
      }
    }
  }, [onLoadMore, totalCount, currentDisplayPage, searchQuery]);

  // Client-side search filtering (only over loaded rows, disabled when using server-side search)
  const searchedTable = useMemo(() => {
    if (useServerSideSearch || !canSearch || !debouncedSearchQuery) return table;

    const filteredRows = table.rows.filter((row) =>
      Object.values(row).some((value) =>
        typeof value === 'string' && value?.toLowerCase()?.includes(debouncedSearchQuery.toLowerCase())
      )
    );

    return { ...table, rows: filteredRows };
  }, [table, canSearch, debouncedSearchQuery, useServerSideSearch]);

  const paginationInfo = useMemo(() => {
    if (!onLoadMore) {
      return null;
    }

    const activeTable = debouncedSearchQuery ? searchedTable : table;

    const effectiveTotalCount = debouncedSearchQuery
      ? activeTable.rows.length
      : (totalCount || activeTable.rows.length);

    // Calculate total pages based on available data
    const loadedPages = Math.ceil(activeTable.rows.length / displayPageSize);

    // For infinite scroll without total count, show a stable page range
    const totalPages = totalCount
      ? Math.ceil(effectiveTotalCount / displayPageSize) // Use exact count when available
      : hasNextPage
        ? Math.max(loadedPages, currentDisplayPage + 2) // Show current + at least 1 ahead when more pages exist
        : loadedPages; // Show only loaded pages when no more exist

    const canPreviousPage = currentDisplayPage > 0;
    const canNextPage = currentDisplayPage < totalPages - 1;
    const needsMoreData = !searchQuery && currentDisplayPage >= loadedPages - 1 && hasNextPage;

    return {
      totalPages,
      currentPage: currentDisplayPage,
      canPreviousPage,
      canNextPage,
      loadedPages,
      needsMoreData,
      entriesStart: (currentDisplayPage * displayPageSize) + 1,
      entriesEnd: Math.min((currentDisplayPage + 1) * displayPageSize, effectiveTotalCount),
      totalCount: totalCount || activeTable.rows.length, // Show known count even if incomplete
    };

  }, [onLoadMore, totalCount, currentDisplayPage, hasNextPage, searchedTable, debouncedSearchQuery, searchQuery, table]);

  const handleDisplayPageChange = (page: number) => {
    if (!onLoadMore) return;

    const activeTable = debouncedSearchQuery ? searchedTable : table;
    const loadedPages = Math.ceil(activeTable.rows.length / displayPageSize);

    // If user is trying to go to a page we don't have data for yet, load more (only when not searching)
    if (!debouncedSearchQuery && page >= loadedPages - 1 && hasNextPage && onLoadMore) {
      setPendingPage(page);
      setPendingPageRowCount(activeTable.rows.length);
      onLoadMore();
    } else {
      setCurrentDisplayPage(page);
    }
  };

  // Slice the data for current display page when pagination is enabled
  const paginatedTableData = useMemo(() => {
    if (!onLoadMore) {
      return searchedTable;
    }

    const activeTable = searchedTable;
    const startIndex = currentDisplayPage * displayPageSize;
    const endIndex = startIndex + displayPageSize;
    const paginatedRows = activeTable.rows.slice(startIndex, endIndex);

    return {
      ...activeTable,
      rows: paginatedRows
    };
  }, [searchedTable, onLoadMore, currentDisplayPage]);

  // Pagination handlers
  const handlePreviousPage = () => {
    if (paginationInfo?.canPreviousPage) {
      handleDisplayPageChange(currentDisplayPage - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationInfo?.canNextPage) {
      handleDisplayPageChange(currentDisplayPage + 1);
    }
  };

  const customSortFunction = (a: any, b: any, key: string, direction: 'asc' | 'desc') => {
    const aValue = a[key];
    const bValue = b[key];

    // Handle date strings (for created_at field)
    if (key === 'created_at' && typeof aValue === 'string' && typeof bValue === 'string') {
      const aDate = new Date(aValue.replace(/ /g, "T"));
      const bDate = new Date(bValue.replace(/ /g, "T"));

      if (aDate.getTime() < bDate.getTime()) {
        return direction === 'desc' ? 1 : -1;
      }
      if (aDate.getTime() > bDate.getTime()) {
        return direction === 'desc' ? -1 : 1;
      }
      return 0;
    }

    if (aValue < bValue) {
      return direction === 'desc' ? 1 : -1;
    }
    if (aValue > bValue) {
      return direction === 'desc' ? -1 : 1;
    }
    return 0;
  };

  const tableData = onLoadMore ? paginatedTableData : searchedTable;





  // Determine the appropriate empty message based on current state
  const getEmptyMessage = () => {
    // Don't show custom messages during loading or error states
    if (dataLoading) return emptyMessage || 'Loading data...';
    if (dataError) return emptyMessage || 'Error loading data.';

    // Only show custom no results messages when we have data but it's filtered out
    // Check if we have search or date filtering active
    const hasSearchFilter = debouncedSearchQuery && debouncedSearchQuery.trim() !== '';
    const hasDateFilter = dateRange && (dateRange.startDate || dateRange.endDate);

    // If searching (text or dates) and zero rows were returned, show specific empty message
    if ((hasSearchFilter || hasDateFilter) && (tableData?.rows?.length ?? 0) === 0) {
      return 'No results found.';
    }

    // Only show filtered messages if we have some data to filter (not completely empty dataset)
    if (hasSearchFilter && hasDateFilter) {
      return 'No results found for the selected date range and search criteria.';
    } else if (hasSearchFilter) {
      return 'No results found for the search criteria.';
    } else if (hasDateFilter) {
      return 'No results found for the selected date range.';
    }

    return emptyMessage || 'No data to display.';
  };



  return (
    <>
      {canSearch && (
        <MDBox pb={2} display="flex" justifyContent="center" alignItems="center" gap={1}>
          {onDateRangeChange && (
            <ClearFiltersButton
              onClick={() => {
                // Clear search
                if (typeof searchValue !== 'string') {
                  setSearchQuery('');
                }
                if (onSearchChange) onSearchChange('');

                // Clear date range
                onDateRangeChange({ startDate: null, endDate: null });

                // Reset pagination state
                setCurrentDisplayPage(0);
                setPendingPage(null);
                setPendingPageRowCount(null);

                // Refocus search input
                inputRef.current?.focus();
              }}
              size="small"
            >
              Clear filters
            </ClearFiltersButton>
          )}
          {onDateRangeChange && (
            <DateRangePicker
              value={dateRange}
              onChange={(newDateRange) => onDateRangeChange(newDateRange)}
              placeholder="Date"
            />
          )}

          <MDInput
            placeholder="Patient Search"
            value={typeof searchValue === 'string' ? searchValue : searchQuery}
            size="small"
            fullWidth
            sx={{ width: 'auto' }}
            inputRef={inputRef}
            onChange={({ currentTarget }) => {
              const value = currentTarget.value;

              // Keep local state in sync for client-side filtering when searchValue is not provided
              if (typeof searchValue !== 'string') {
                setSearchQuery(value);
              }
              if (onSearchChange) onSearchChange(value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="medium" />
                </InputAdornment>
              ),
            }}
          />

          {user?.can(ActionSlugs.CREATE$APPOINTMENT) && onAddExam && (
            <MDButton
              variant="contained"
              color={MUIColors.SECONDARY}
              onClick={onAddExam}
            >
              <FontAwesomeIcon color={colors.white.focus} icon={faPlus} style={{ marginRight: '10px' }} />
              New Evaluation
            </MDButton>
          )}
        </MDBox>
      )}
      <DataTable
        entriesPerPage={entriesPerPage}
        showTotalEntries={onLoadMore ? false : showTotalEntries} // We handle this in PaginationControls when pagination is enabled
        table={tableData}
        isSorted={isSorted}
        noEndBorder={noEndBorder}
        dataLoading={dataLoading} // Controlled by parent; stays mounted on empty
        dataError={dataError}
        handleRetry={handleRetry}
        secondaryAction={secondaryAction}
        handleRowClick={handleRowClick}
        emptyMessage={getEmptyMessage()}



        verticalAlign={verticalAlign}
        initialSort={initialSort}
        customSortFunction={customSortFunction}
        getRowKey={(row) => {
          return `appointment-${row.appointment_id}`
        }}
      />

      {onLoadMore && paginationInfo && (
        <PaginationControls
          currentPage={paginationInfo.currentPage}
          totalPages={paginationInfo.totalPages}
          totalCount={paginationInfo.totalCount}
          pageSize={displayPageSize}
          onPageChange={handleDisplayPageChange}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          showTotalEntries={true}
          isLoading={isFetchingNextPage}
        />
      )}

      {onLoadMore && isFetchingNextPage && (
        <MDBox
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={1}
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 1,
            mt: 1
          }}
        >
          <MDTypography variant="caption" color="text" sx={{ opacity: 0.7 }}>
            Loading more triage data...
          </MDTypography>
        </MDBox>
      )}
    </>
  );
};

export default TriageDataTable;